"use client";

import Link from "next/link";
import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceBenefitItem,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";

const TRAINING_WHATSAPP_MESSAGE =
  "Hi Anchor Sprint, I'd like to ask about HRD Corp claimable AI training for my team (from your AI Training page).";

const trainingHeroImage = "/images/services/svc-training-education.jpg";

// Breaks a band section out of the layout's centered container to full viewport width.
const FULL_BLEED = "mx-[calc(50%_-_50vw)]";

// HRD Corp levy claim process — the page's strongest differentiator.
const hrdcEligibility = [
  "Employers registered with HRD Corp who contribute the monthly HRD levy",
  "Malaysian companies with 10 or more local employees register and contribute by default; smaller firms in covered sectors can opt in voluntarily",
  "Levy balances can be applied directly to course fees — so training is effectively pre-funded",
];

const hrdcClaimSteps = [
  {
    title: "Check your levy balance",
    description:
      "Log in to the eTRiS portal to confirm the levy amount available for training.",
  },
  {
    title: "Pick a course and get our quotation",
    description:
      "We prepare the HRD Corp-compliant quotation, course outline, and trainer profile for you.",
  },
  {
    title: "Apply for the training grant",
    description:
      "Submit the grant application in eTRiS before the course start date — we help with the paperwork.",
  },
  {
    title: "Attend and complete the training",
    description:
      "Participants meet the attendance requirements, with records captured for the claim.",
  },
  {
    title: "Submit your claim",
    description:
      "After training, submit the claim with the invoice and attendance records — HRD Corp reimburses from your levy.",
  },
];

// Per-course detail cards: duration, audience, and outcomes.
const flagshipCourses = [
  {
    title: "AI Fluency for Business Teams",
    description:
      "AI literacy for non-technical teams, taught on the 4D AI Fluency framework: how to delegate work to AI, describe it well, judge what comes back, and stay accountable for it.",
    duration: "2 days",
    audience: "Managers, team leads and non-technical staff",
    outcomes: "Identify high-impact AI use cases and leave with a practical adoption plan",
  },
  {
    title: "Digital Transformation for Malaysian Businesses",
    description:
      "Hands-on guidance to assess digital maturity, explore practical tools, and build a step-by-step transformation roadmap without jargon.",
    duration: "2 days",
    audience: "Business owners and operations managers",
    outcomes: "A digital maturity assessment and a step-by-step transformation roadmap",
  },
  {
    title: "Practical Financial Solutions & Credit Readiness",
    description:
      "Understand financial health, financing options beyond loans, and practical tools that strengthen cash flow, reporting, and credit readiness.",
    duration: "1 day",
    audience: "Founders, finance teams and business owners",
    outcomes: "Stronger cash flow visibility, reporting habits, and credit readiness",
  },
];

const courseMetaLabels = ["Duration", "Audience", "Outcomes"] as const;

const trainingFAQs = [
  {
    question: "Is your AI training HRD Corp claimable?",
    answer: "Yes. Anchor Sprint is an HRD Corp approved training provider, and our AI courses are designed to be claimable under SBL Khas against the HRDF levy. Registered employers can apply for a training grant through eTRiS before the course starts, and we provide the quotation, course outline, and trainer profile needed for the application."
  },
  {
    question: "How does the HRD Corp claim process work?",
    answer: "Your company applies for a grant via the eTRiS portal before training begins, attends the course with the required attendance records, then submits a claim after completion. HRD Corp reimburses the approved amount from your levy account. We guide you through every step and prepare the supporting documents."
  },
  {
    question: "What is SBL Khas, and is your AI training claimable under it?",
    answer: "SBL Khas (Skim Bantuan Latihan Khas) is the HRD Corp scheme most employers use for external training: you apply for the grant in eTRiS before the course starts, and HRD Corp reimburses the approved amount from the levy your company already contributes. Our AI courses are built to be claimable under it, and we supply the quotation, course outline and trainer profile the application needs."
  },
  {
    question: "Is this AI literacy training or AI fluency training?",
    answer: "Both terms describe the same goal, and we use the AI Fluency framework published by Anthropic as the structure. It is built on four competencies — Delegation, Description, Discernment and Diligence — which is a more useful shape than a general 'what is AI' overview because each one maps to something a person does at work. Our trainers have completed the AI Fluency: Framework & Foundations course."
  },
  {
    question: "What makes your AI training different from other HRD Corp providers?",
    answer: "Most training providers teach AI from the outside. We are Certified in the Claude Partner Network with Claude Certified Architects on the team, and we deploy production AI agents for Malaysian and Singaporean businesses — so the examples in the room are systems we actually built, including what went wrong in them. We are not an official, exclusive or endorsed Anthropic representative."
  },
  {
    question: "Can you train non-technical staff, not just the IT team?",
    answer: "Yes — that is most of our work. The AI Fluency course is written for managers, executives and operations staff with no coding background. Technical depth is available as a separate track for developers who want the Claude Agent SDK and Model Context Protocol."
  },
  {
    question: "What is the maximum class size?",
    answer: "We recommend 15-25 participants per cohort so workshops stay hands-on, with time for every participant to apply the tools to their own business. For larger groups we can run multiple cohorts or parallel tracks with additional facilitators."
  },
  {
    question: "Do you offer online or in-person delivery?",
    answer: "Both. Courses can be delivered on-site at your office, at a training venue, fully online, or as blended cohorts. Remote online delivery is supported under HRD Corp rules, and we adapt every exercise so virtual sessions stay practical and interactive."
  },
  {
    question: "Do participants receive a certificate?",
    answer: "Yes. Every participant who completes a course receives a certificate of completion from Anchor Sprint, which supports HRD Corp claim documentation and your internal training records."
  }
];

export default function TrainingEducation() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);

  return (
    <ServicePageLayout
      title="HRD Corp Claimable AI Training in Malaysia"
      breadcrumb="Services / Training & Education"
      currentServiceId="te"
      accent="AI Training"
      dek="Anchor Sprint is an HRD Corp approved training provider delivering AI training for Malaysian businesses — claimable under SBL Khas against the HRDF levy you already pay. Our curriculum follows the AI Fluency framework published by Anthropic, and our trainers have completed it. Every cohort is practitioner-led, case-based, and built around the workflows your team actually runs."
      ctaLabel="Enquire About Custom Training"
      heroActions={
        <WhatsAppCta
          message={TRAINING_WHATSAPP_MESSAGE}
          location="training_hero"
          label={translate("WhatsApp us about training")}
          className="w-full sm:w-auto"
        />
      }
    >
      <ServiceSection
        title="Why Malaysian teams choose our AI training"
        eyebrow="Why Anchor Sprint"
        accent="Anchor Sprint Training"
        dek="HRD Corp approved, taught on the AI Fluency framework, and structured around outcomes your management can measure."
        band="ivory"
        className={FULL_BLEED}
      >
        <div className="mb-10 rounded-2xl border border-border bg-card p-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-neutral-muted">
                {translate("Program Snapshot")}
              </p>
              <h3 className="mt-2 font-display text-2xl font-semibold text-neutral-emphasis">
                {translate("Three AI training courses ready to run, with custom cohorts available")}
              </h3>
              <p className="mt-2 max-w-2xl text-neutral-muted">
                {translate(
                  "Practical AI training for Malaysian businesses — AI fluency for everyday teams, digital transformation and finance readiness. All HRD Corp claimable.",
                )}
              </p>
              <p className="mt-2 max-w-2xl text-sm text-neutral-muted">
                {translate("Budget 2027 puts AI skills on the national agenda, with 5,000 AI professionals to be trained and certified. ")}
                <Link
                  href={getLocalizedPathname("/guides/malaysia-budget-2027/", locale)}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {translate("See the Budget 2027 highlights")}
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {["AI Fluency", "Digital Transformation", "Finance & Credit Readiness"].map(
                (label) => (
                  <span
                    key={label}
                    className="rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary"
                  >
                    {translate(label)}
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        <ServiceGrid columns={3}>
          <ServiceFeatureCard
            icon="fas fa-certificate"
            title="HRD Corp approved, SBL Khas claimable"
            description="Registered as an HRD Corp training provider, so programmes can be claimed under SBL Khas against your HRDF levy instead of paid for twice."
          />
          <ServiceFeatureCard
            icon="fas fa-chalkboard-teacher"
            title="Taught on the AI Fluency framework"
            description="Our trainers have completed the AI Fluency: Framework & Foundations course published by Anthropic, and teach the same 4D model — Delegation, Description, Discernment, Diligence."
          />
          <ServiceFeatureCard
            icon="fas fa-briefcase"
            title="Taught by people who ship agents"
            description="We are Certified in the Claude Partner Network and deploy production AI agents. Your team learns from the engineers who build them, not from a slide deck."
          />
        </ServiceGrid>
      </ServiceSection>

      <ServiceSection
        title="HRD Corp claimable: how the HRDF levy funds your AI training"
        eyebrow="HRD Corp funding"
        accent="HRD Corp Levy"
        dek="Most Malaysian employers can claim our AI courses under SBL Khas against the HRD Corp (HRDF) levy — here is exactly how the process works."
        band="surface"
        className={FULL_BLEED}
      >
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <h3 className="mb-5 font-display text-xl font-bold text-neutral-emphasis">
              {translate("Who is eligible")}
            </h3>
            <ul className="space-y-4">
              {hrdcEligibility.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                    aria-hidden
                  />
                  <span className="leading-relaxed text-neutral-muted">
                    {translate(point)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-8 rounded-2xl border border-border bg-card p-5 text-sm leading-relaxed text-neutral-muted">
              {translate(
                "Not sure about your eligibility or levy balance? Send us a message and we will walk you through it before you commit to anything.",
              )}
            </p>
          </div>

          <div>
            <h3 className="mb-5 font-display text-xl font-bold text-neutral-emphasis">
              {translate("How to claim, step by step")}
            </h3>
            <ol className="divide-y divide-border border-y border-border">
              {hrdcClaimSteps.map((step, index) => (
                <li key={step.title} className="flex gap-5 py-5">
                  <span className="font-mono text-sm font-bold text-primary">
                    0{index + 1}
                  </span>
                  <div>
                    <h4 className="mb-1 font-semibold text-neutral-emphasis">
                      {translate(step.title)}
                    </h4>
                    <p className="text-sm leading-relaxed text-neutral-muted">
                      {translate(step.description)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </ServiceSection>

      <ServiceSection
        title="AI training courses for Malaysian businesses"
        eyebrow="Courses"
        accent="Flagship Courses"
        dek="Who each programme is for, how long it runs, and what your team walks away with."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceGrid columns={3}>
          {flagshipCourses.map((course, index) => {
            const meta: Record<(typeof courseMetaLabels)[number], string> = {
              Duration: course.duration,
              Audience: course.audience,
              Outcomes: course.outcomes,
            };

            return (
              <div
                key={course.title}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <span className="mb-5 font-mono text-sm text-neutral-muted/60">
                  0{index + 1}
                </span>
                <h3 className="mb-3 font-display text-xl font-semibold text-neutral-emphasis">
                  {translate(course.title)}
                </h3>
                <p className="mb-6 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {translate(course.description)}
                </p>
                <dl className="divide-y divide-border border-t border-border">
                  {courseMetaLabels.map((label) => (
                    <div key={label} className="py-3">
                      <dt className="mb-1 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-neutral-muted">
                        {translate(label)}
                      </dt>
                      <dd className="text-sm text-neutral-emphasis">
                        {translate(meta[label])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
        </ServiceGrid>

        {/* Editorial cross-link into the Academy hub — new pages need inbound links to get indexed */}
        <p className="mt-10 max-w-3xl text-neutral-muted">
          {translate(
            "Preparing your team for a Claude certification, or taking non-engineers all the way to a production app? Those programmes run under",
          )}{" "}
          <Link
            href={getLocalizedPathname("/academy/", locale)}
            className="font-medium text-primary underline underline-offset-4"
          >
            {translate("Anchor Academy")}
          </Link>
          {translate(
            " — Claude certification prep and the Vibe Engineering series, HRD Corp claimable, with the claim filed for you.",
          )}
        </p>

        {/* ChatGPT team workshops: /academy/chatgpt-training/ owns the ChatGPT
            training terms (2026-10-08). This page keeps the general "HRD Corp
            claimable AI training" and links across rather than restating. */}
        <p className="mt-6 max-w-3xl text-neutral-muted">
          {translate(
            "Rolling out ChatGPT Business or Enterprise? We also run hands-on ChatGPT workshops for teams, HRD Corp claimable, built around the work each team does every week. See",
          )}{" "}
          <Link
            href={getLocalizedPathname("/academy/chatgpt-training/", locale)}
            className="font-medium text-primary underline underline-offset-4"
          >
            {translate("ChatGPT training for teams")}
          </Link>
          {translate(".")}
        </p>
      </ServiceSection>

      <ServiceSection
        title="Custom Training & Delivery Options"
        eyebrow="Delivery"
        accent="Delivery Options"
        dek="On-site, virtual, or blended — tailored to your industry and measured with assessments."
        band="surface"
        className={FULL_BLEED}
      >
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="grid grid-cols-1 gap-4">
            <ServiceBenefitItem
              title="Tailored curriculum design"
              description="Adapt modules to your industry, systems, and transformation roadmap."
              icon="fas fa-check-circle"
            />
            <ServiceBenefitItem
              title="Flexible delivery formats"
              description="On-site, virtual, or blended cohorts to suit busy teams and multiple locations."
              icon="fas fa-check-circle"
            />
            <ServiceBenefitItem
              title="Pre- and post-training assessments"
              description="Baseline diagnostics and impact reporting to prove ROI."
              icon="fas fa-check-circle"
            />
            <ServiceBenefitItem
              title="Enablement beyond the classroom"
              description="Playbooks, templates, and follow-up clinics to embed new capabilities."
              icon="fas fa-check-circle"
            />
          </div>

          {/* Framed media — the page's visual anchor below the hero */}
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src={trainingHeroImage}
              alt={translate(
                "HRD Corp certified training partner for AI and digital transformation",
              )}
              loading="lazy"
              width={800}
              height={400}
              decoding="async"
              className="h-full min-h-[280px] w-full object-cover"
            />
          </div>
        </div>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        eyebrow="FAQ"
        accent="Questions"
        dek="HRD Corp claims, class sizes, delivery formats, and certification — answered."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceFaq items={trainingFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={trainingFAQs} />

      <ConsultationCTA
        title="Need a different course? We will tailor it for you."
        description="Tell us your training goals and we will design a custom programme across AI, digital transformation, or modern finance."
        primaryLabel="Enquire About Custom Training"
        primaryHref="/contact/"
        whatsapp={{
          message: TRAINING_WHATSAPP_MESSAGE,
          location: "training_closing_cta",
          label: "WhatsApp us about training",
        }}
      />
    </ServicePageLayout>
  );
}
